import { useDrag, useDrop, XYCoord } from "react-dnd";
import { useRef } from "react";
import { NamesOfChangedKeys } from "../ConfigDataElem/ConfigDataElem.props";
import {
	reorderSlaveDataElement,
	reorderSlaveDataOrCondition,
	replaceEndpointOrConditionsRunInEntryList,
	replaceEndpointRunInEntryList,
} from "@/store/ses/sesItemConfigSlice";
import { swapElementsInArray } from "@/helpers";
import { IConditionConfirmation, IConditionData, IConditionInterval, IConditionVariable } from "@/types/ses/sesItemConfigTypes";

interface ConditionItem {
	conditionIdx: number;
	conditionBlockIdx: number;
	orBlockIndex?: number;
	dragIdx?: number;
}

export const useConditionDragDrop = ({
	conditionIdx,
	conditionBlockIdx,
	orBlockIndex,
	dragIdx,
	conditionFor,
	changeFlgHandler,
	dispatch,
	endpointElem,
	conditionWithOrOption,
	conditionEntryIdx,
}: {
	conditionIdx: number;
	conditionBlockIdx: number;
	orBlockIndex?: number;
	dragIdx?: number;
	conditionFor: any;
	changeFlgHandler: (changed: NamesOfChangedKeys) => void;
	dispatch: any;
	endpointElem: any;
	conditionWithOrOption?: boolean;
	conditionEntryIdx?: number | null;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const rafRef = useRef<number | null>(null);

	// DRAG
	const [, drag] = useDrag({
		type: "condition-item",
		item: () => ({
			conditionIdx,
			conditionBlockIdx,
			orBlockIndex,
			dragIdx,
		}),
	});

	// DROP
	const [, drop] = useDrop<ConditionItem>({
		accept: "condition-item",
		hover(item, monitor) {
			if (!ref.current) return;

			// Индекс того, что тащим
			const dragIndex = typeof item.dragIdx === "number" ? item.dragIdx : item.conditionIdx;
			// Индекс того, на что навели
			const hoverIndex = typeof dragIdx === "number" ? dragIdx : conditionIdx;

			if (dragIndex === hoverIndex) return;

			const hoverRect = ref.current.getBoundingClientRect();
			const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
			const clientOffset = monitor.getClientOffset();
			if (!clientOffset) return;
			const hoverClientY = (clientOffset as XYCoord).y - hoverRect.top;

			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

			// OR-БЛОК

			if (
				conditionFor.for === "dataElemSlaveData" &&
				typeof item.dragIdx === "number" &&
				typeof item.orBlockIndex === "number" &&
				typeof orBlockIndex === "number" &&
				!conditionWithOrOption
				// item.orBlockIndex === orBlockIndex &&
				// "conditionEntryBlockIdx" in conditionFor
			) {
				dispatch(
					reorderSlaveDataOrCondition({
						conditionEntryBlockIdx: conditionFor.conditionEntryBlockIdx,
						conditionBlockIdx,
						conditionIdx,
						orBlockIndex,
						fromIdx: dragIndex,
						toIdx: hoverIndex,
					})
				);
				// Обновляем dragIdx у перетаскиваемого элемента
				item.dragIdx = hoverIndex;
				changeFlgHandler("slaveData");
				return;
			}

			// DATA ELEMENT SLAVE DATA
			if (conditionFor.for === "dataElemSlaveData") {
				dispatch(
					reorderSlaveDataElement({
						conditionBlockIdx,
						fromIdx: dragIndex,
						toIdx: hoverIndex,
					})
				);
				item.conditionIdx = hoverIndex;
				changeFlgHandler("slaveData");
				return;
			}

			if (item.conditionBlockIdx !== conditionBlockIdx) return;

			if (
				conditionFor.for === "endpointRunInEntry" &&
				typeof item.dragIdx === "number" &&
				typeof item.orBlockIndex === "number" &&
				typeof conditionEntryIdx === "number"
			) {
				const entryIdx = conditionFor.conditionEntryBlockIdx;
				const entry = endpointElem.endpoint?.entry[entryIdx];
				if (!entry) return;

				const block = entry.run[conditionBlockIdx][conditionIdx];

				if (!Array.isArray(block.value)) return;

				// экранируем частые hover
				if (rafRef.current !== null) return;

				rafRef.current = requestAnimationFrame(() => {
					rafRef.current = null;

					// защита от бессмысленного swap
					if (dragIndex === hoverIndex) return;

					const swapedArr = swapElementsInArray(block.value, dragIndex, hoverIndex);

					dispatch(
						replaceEndpointOrConditionsRunInEntryList({
							conditionEntryBlockIdx: conditionEntryIdx,
							conditionEntryIdx: conditionBlockIdx,
							conditionIdx,
							runList: swapedArr as (IConditionInterval | IConditionVariable | IConditionData | IConditionConfirmation)[][],
						})
					);

					// обновляем позицию перетаскиваемого элемента
					item.dragIdx = hoverIndex;
					changeFlgHandler("entry");
				});

				return;
			}
			//  AND-БЛОК

			if (conditionFor.for === "endpointRunInEntry" && typeof conditionEntryIdx === "number") {
				const entryIdx = conditionFor.conditionEntryBlockIdx;
				const entry = endpointElem.endpoint?.entry[entryIdx];

				if (!entry) return;

				const block = entry.run[conditionBlockIdx];

				if (!Array.isArray(block)) return;

				// Перестановка
				const swapedArr = swapElementsInArray(block, dragIndex, hoverIndex);

				dispatch(
					replaceEndpointRunInEntryList({
						conditionEntryBlockIdx: conditionEntryIdx,
						conditionEntryIdx: conditionBlockIdx,
						runList: swapedArr,
					})
				);

				item.conditionIdx = hoverIndex;
				changeFlgHandler("entry");
				return;
			}
		},
	});

	drag(drop(ref)); // соединяем drag + drop

	return ref;
};
